/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * StudentCoachMessages - Shows announcements/messages from coaches
 * Displays on student dashboard when they have coach messages
 */

import { useState, useEffect } from 'react'
import {
  MessageSquare,
  User,
  Calendar,
  CheckCircle,
  Loader2,
  ChevronDown,
  ChevronUp,
  Bell
} from 'lucide-react'
import api from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { createLogger } from '../utils/logger'

const logger = createLogger('StudentCoachMessages')

const StudentCoachMessages = ({ coachId = null, showEmpty = false }) => {
  const { fetchCoachNotificationCount } = useAuthStore()
  const [announcements, setAnnouncements] = useState([])
  const [loading, setLoading] = useState(true)
  const [unreadCount, setUnreadCount] = useState(0)
  const [expanded, setExpanded] = useState(true)

  useEffect(() => {
    loadAnnouncements()
    loadUnreadCount()
  }, [coachId])

  const loadAnnouncements = async () => {
    try {
      setLoading(true)
      const params = { limit: 5 }
      if (coachId) {
        params.coachId = coachId
      }
      const response = await api.get('/user/coach-announcements', { params })

      if (response.data.success) {
        setAnnouncements(response.data.announcements || [])
      }
    } catch (error) {
      logger.error('Error loading coach announcements:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadUnreadCount = async () => {
    try {
      const params = {}
      if (coachId) {
        params.coachId = coachId
      }
      const response = await api.get('/user/coach-announcements/count', { params })
      if (response.data.success) {
        setUnreadCount(response.data.unreadCount || 0)
      }
    } catch (error) {
      logger.error('Error loading unread count:', error)
    }
  }

  const markAsRead = async (announcementId) => {
    try {
      await api.patch(`/user/coach-announcements/${announcementId}/read`)
      // Update local state
      setAnnouncements(
        announcements.map((a) => (a.id === announcementId ? { ...a, isRead: true } : a))
      )
      setUnreadCount(Math.max(0, unreadCount - 1))
      fetchCoachNotificationCount() // Update sidebar badge
    } catch (error) {
      logger.error('Error marking announcement as read:', error)
    }
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays === 0) {
      return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    } else if (diffDays === 1) {
      return 'Yesterday'
    } else if (diffDays < 7) {
      return `${diffDays} days ago`
    }
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  }

  if (loading) {
    return (
      <div className="card">
        <div className="card-body flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-600 dark:text-gray-400">Loading announcements...</span>
        </div>
      </div>
    )
  }

  if (announcements.length === 0) {
    if (!showEmpty) {
      return null // Don't show anything if no messages (dashboard mode)
    }
    // Show empty state for My Coach page
    return (
      <div className="card">
        <div className="card-body text-center py-8">
          <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
          <p className="text-gray-500 dark:text-gray-400">No announcements</p>
        </div>
      </div>
    )
  }

  return (
    <div className="card border-l-4 border-l-primary-500">
      <div className="card-body">
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between mb-4"
        >
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Announcements
            </h3>
            {unreadCount > 0 && (
              <span className="text-xs px-2.5 py-1 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium">
                {unreadCount} new
              </span>
            )}
          </div>
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {expanded && (
          <div className="space-y-3">
            {announcements.map((announcement) => (
              <div
                key={announcement.id}
                className={`border rounded-lg p-4 transition-all duration-200 ${
                  announcement.isRead
                    ? 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900'
                    : 'border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/20'
                }`}
                onClick={() => !announcement.isRead && markAsRead(announcement.id)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                        {announcement.title}
                      </h4>
                      {!announcement.isRead && (
                        <span className="w-2 h-2 rounded-full bg-primary-500" />
                      )}
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap line-clamp-3">
                      {announcement.content}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {announcement.coach?.firstName} {announcement.coach?.lastName}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(announcement.publishedAt)}
                      </span>
                    </div>
                  </div>
                  {announcement.isRead && (
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default StudentCoachMessages
